import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { gql, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { TenantDataEntityExtended } from 'mj_generatedentities';
import { RunView } from '@memberjunction/core';

/**
 * Common fields shared by both "linked" and "supported" HotGlue sources.
 */
export interface HotGlueSourceBase {
  auth_url: string;
  capabilities: string[];
  client_id: string;
  connector_props: Record<string, unknown>;
  connector_repo: string;
  connector_tag: string;
  domain: string;
  fieldMap: Record<string, unknown>;
  has_triggers: boolean;
  icon: string;
  label: string;
  realtimeDiscover: boolean;
  schemaTables: string[];
  tags: string[];
  tap: string;
  tap_url: string;
  type: string;
  version: string;
}

/**
 * "Linked" sources have additional fields like connection_name, tenant_id, etc.
 */
export interface HotGlueLinkedSource extends HotGlueSourceBase {
  connection_name: string;
  forkedFieldMap: boolean;
  isReconnecting: boolean;
  tenant_id: string;
}

/**
 * "Supported" sources often include discover-task info and a flag for schema.
 */
export interface HotGlueSupportedSource extends HotGlueSourceBase {
  discover_task_id: string;
  discover_task_status: string;
  discover_task_timestamp: string;
  is_hg_schema_enabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConnectorService {
  private scriptLoaded = false;
  // private _HotGlue: any;
  private supportedSources: any[] = [];
  private linkedSources: any[] = [];
  private currentTenant: TenantDataEntityExtended;

  // Cache for the tenant data by instanceID.
  private tenantMap: Map<string, TenantDataEntityExtended> = new Map();
  // Cache for the HotGlue mounts per tenant.
  private hotGlueMounts: Map<string, any> = new Map();
  // Cache for the HotGlue JWT tokens per tenant.
  private tenantJWTCached: Map<string, { token: string; expiresAt: number }> = new Map();

  constructor() {
    // this.organizationService.MetadataLoadingStage$.subscribe(async (loaded) => {
    //   if (loaded < MetadataLoadingStage.InstanceConnectorsLoaded) {
    //     // Reset the linked sources when metadata is reloaded
    //     this.resetTenant();
    //   } else if (loaded === MetadataLoadingStage.InstanceConnectorsLoaded) {
    //     // Load the tenant data when metadata is loaded
    //     this.tenant = await this.GetParentTenantData(organizationService.CurrentInstance.ID);
    //   }
    // });
  }

  public get HotGlue(): any {
    if (!this.currentTenant) {
      return null;
    }

    return this.hotGlueMounts.get(this.currentTenant.ID);
  }

  public resetLinkiedSources(): void {
    this.linkedSources = [];
  }

  /**
   * Sets the current tenant based on the instance ID.
   * If the tenant already exists in the cache, it will be used.
   * Otherwise, it will be fetched from the server.
   * 
   * @param instanceID The instance ID to set the tenant for.
   * 
   */
  public async SetCurrentTenant(instanceID: string): Promise<void> {
    this.currentTenant = await this.getParentTenantData(instanceID);
  }

  // public resetTenant(): void {
  //   this.resetLinkiedSources();
  //   this.tenant = null;
  // }

  /**
   * Dynamically load the HotGlue script from https://hotglue.xyz/widgetv2.js
   */
  public async loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded) {
        // Script already loaded; no need to do it again
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://hotglue.xyz/widgetv2.js';
      script.async = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('HotGlue script failed to load'));
      document.body.appendChild(script);
    });
  }

  /**
   * Mount HotGlue after script is loaded
   */
  public async mountHotGlue(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (!this.scriptLoaded) {
        return reject(new Error('HotGlue script not loaded yet'));
      }

      if (!this.currentTenant) {
        return reject(new Error('Tenant data is not loaded yet.'));
      }

      if (this.hotGlueMounts.has(this.currentTenant.ID)) {
        // Already mounted
        return resolve();
      }

      const w = (window as any);
      if (!w.HotGlue) {
        return reject(new Error('HotGlue is not available on window'));
      }

      const hotGlue = w.HotGlue;
      const token = await this.GetTenantJWT(this.currentTenant.ID);

      // Mount with your environment variables
      hotGlue.mount({
        api_key: environment.HOTGLUE_PUBLISHABLE_KEY,
        env_id: environment.HOTGLUE_ENV_ID,
        jwtToken: token
      });

      // Poll until it reports that it has mounted
      const checkMount = setInterval(() => {
        if (hotGlue.hasMounted && hotGlue.hasMounted()) {
          clearInterval(checkMount);
          this.hotGlueMounts.set(this.currentTenant.ID, hotGlue);
          resolve();
        }
      }, 50);
    });
  }

  /**
   * Fetch the list of supported sources (connectors)
   */
  public async getSupportedSources(flowId: string): Promise<HotGlueSupportedSource[]> {
    const hotGlue = this.HotGlue;

    if (!hotGlue) {
      return Promise.reject('HotGlue is not mounted yet.');
    }

    if (this.supportedSources && this.supportedSources.length > 0) {
      return this.supportedSources;
    }

    const sources = await hotGlue.getSupportedSources(flowId);

    if (sources && sources.length > 0) {
      this.supportedSources = sources;
      return sources;
    }

    return [];
  }

  /**
   * Fetch the list of linked sources
   */
  // public async getLinkedSources(flowId: string): Promise<any[]> {
  //   const hotGlue = this.HotGlue;

  //   if (!hotGlue) {
  //     return Promise.reject('HotGlue is not mounted yet.');
  //   }

  //   if (this.linkedSources && this.linkedSources.length > 0) {
  //     return this.linkedSources;
  //   }

  //   const sources = await hotGlue.getLinkedSources(flowId, this.currentTenant.TenantName);

  //   if (sources && sources.length > 0) {
  //     this.linkedSources = sources;
  //     return sources;
  //   }

  //   return [];
  // }

  /**
   * Example: Retrieve linked sources by calling the HotGlue public API directly.
   * @Param flowId The HotGlue flow ID to fetch linked sources for.
   */
  public async getLinkedSources(flowId: string): Promise<HotGlueLinkedSource[]> {
    // 1) Check if we've already cached the linked sources
    if (this.linkedSources && this.linkedSources.length > 0) {
      return this.linkedSources;
    }

    // 2) Generate/fetch the JWT for this tenant
    const token = await this.GetTenantJWT(this.currentTenant.ID);

    // 3) Construct the API endpoint per HotGlue docs:
    //    GET https://client-api.hotglue.xyz/<env_id>/<flow_id>/<tenant_id>/linkedSources?token=<jwt>
    const baseUrl = 'https://client-api.hotglue.xyz';
    const envId = environment.HOTGLUE_ENV_ID;
    const tenantId = this.currentTenant.TenantName;
    const apiKey = environment.HOTGLUE_PUBLISHABLE_KEY;
    const url = `${baseUrl}/${envId}/${flowId}/${tenantId}/linkedSources?token=${token}`;

    // 4) Make the request using fetch
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'x-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch linked sources: ${response.status} ${response.statusText}`);
      }

      // 5) Parse the JSON response and cache it
      const sources = await response.json();
      this.linkedSources = sources;
      return sources;
    } catch (error) {
      console.error('Error fetching linked sources:', error);
      return [];
    }
  }

  /**
   * Open the default HotGlue widget flow
   */
  public open(flowId: string, multipleSources: boolean = false): Promise<void> {
    const hotGlue = this.HotGlue;

    if (!hotGlue) {
      return Promise.reject('HotGlue is not mounted yet.');
    }

    return new Promise((resolve, reject) => {
      this.GetTenantJWT(this.currentTenant.ID).then(token => {
        hotGlue.open(this.currentTenant.TenantName, {
          multiplesources: multipleSources,
          hideBackButtons: true,
          flow: flowId,
          jwtToken: token
        });
        resolve();
      }).catch(err => {
        reject(err);
      });
    });
  }

  /**
   * Link a specific connector (CTA approach)
   */
  public link(flowId: string, source: string, options: any = {}): Promise<void> {
    const hotGlue = this.HotGlue;

    if (!hotGlue) {
      return Promise.reject(new Error('HotGlue is not mounted yet.'));
    }

    return new Promise((resolve, reject) => {
      this.GetTenantJWT(this.currentTenant.ID).then(token => {
        this.HotGlue.link(
          this.currentTenant.TenantName,
          flowId,
          source,
          false, // control flow
          {
            hideBackButtons: true,
            jwtToken: token,
            listener: {
              onSourceLinked: (source: string, flow_id: string) => {
                // If caller provides a custom callback, call it
                if (options.listeners && typeof options.listeners.onSourceLinked === 'function') {
                  options.listeners.onSourceLinked(source, flow_id);
                }
                resolve();
              },

              onSourceLinkCanceled: (source: string, flow_id: string) => {
                if (options.listeners && typeof options.listeners.onSourceLinkCanceled === 'function') {
                  options.listeners.onSourceLinkCanceled(source, flow_id);
                }
                reject(new Error('User cancelled the connector link flow'));
              },

              onSourceUnlinked: (source: string, flow_id: string) => {
                if (options.listeners && typeof options.listeners.onSourceUnlinked === 'function') {
                  options.listeners.onSourceUnlinked(source, flow_id);
                }
                reject(new Error('User cancelled the connector link flow'));
              },

              onPopupClose: (source: string, flow_id: string) => {
                if (options.listeners && typeof options.listeners.onPopupClose === 'function') {
                  options.listeners.onPopupClose(source, flow_id);
                }
                reject(new Error('User closed the connector link flow'));
              }
            }
          }
        );
      }).catch(err => {
        reject(err);
      });
    });
  }

  private async getParentTenantData(instanceID: string): Promise<TenantDataEntityExtended> {
    if (!instanceID) {
      return null;
    }

    if (this.tenantMap.has(instanceID)) {
      return this.tenantMap.get(instanceID);
    }

    const rv = new RunView();
    const result = await rv.RunView<TenantDataEntityExtended>({
      'EntityName': "Tenant Datas",
      'ExtraFilter': `InstanceID = '${instanceID}' AND ParentID IS NULL`,
      'ResultType': 'entity_object'
    });

    if (result.Success && result.Results.length > 0) {
      const tenant = result.Results[0];
      this.tenantMap.set(instanceID, tenant);
      return tenant;
    }

    return null;
  }

  /**
   * Generates a HotGlue tenant JWT for a given tenant ID.
   * Caches the token per tenant until it expires (approximately 1 hour).
   */
  private async GetTenantJWT(tenantID: string): Promise<string> {
    const now = Date.now();
    const cached = this.tenantJWTCached.get(tenantID);
    if (cached && cached.expiresAt > now) {
      return cached.token;
    }

    // Otherwise, retrieve a new token from the GraphQL resolver.
    const query = gql`
      query GenerateTenantJWT($TenantID: String!) {
        GenerateTenantJWT(TenantID: $TenantID)
      }
    `;
    const result = await GraphQLDataProvider.Instance.ExecuteGQL(query, { TenantID: tenantID });
    const token = result.GenerateTenantJWT;
    // Cache token for 55 minutes (assuming a 1-hour validity).
    this.tenantJWTCached.set(tenantID, { token, expiresAt: now + 55 * 60 * 1000 });
    return token;
  }
}
